"use client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { formatCurrency } from "@/utils/formatCurrency";
import { trimText } from "@/lib/trimText";
import Image from "next/image";
import { Eye, ShoppingCart, CheckCircle2, XCircle, BookOpen, Calendar, Hash, Layers } from "lucide-react";
import { useState } from "react";

const StockStatus = ({ inStock }) => {
  return (
    <div className="flex items-center gap-2">
      {inStock ? (
        <>
          <CheckCircle2 size={16} className="text-green-600" />
          <span className="text-sm font-medium text-green-700">In Stock</span>
        </>
      ) : (
        <>
          <XCircle size={16} className="text-red-500" />
          <span className="text-sm font-medium text-red-600">Out of Stock</span>
        </>
      )}
    </div>
  );
};

const ProductQuickView = ({ product, onAddToCart }) => {
  const isInStock = product?.countInStock > 0;
  const [imgSrc, setImgSrc] = useState(product?.images?.[0] || "/placeholder.jpg");

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button className="flex h-8 w-8 items-center justify-center bg-white shadow-md transition-colors hover:bg-gray-50 border border-gray-200">
          <Eye className="h-4 w-4 text-[#0A3982]" />
        </button>
      </DialogTrigger>
      
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto p-0 border-gray-200 rounded-none shadow-2xl">
        <DialogHeader className="p-6 pb-2 border-b border-gray-200">
          <DialogTitle className="text-2xl font-bold text-gray-900 leading-tight">
            {product?.title}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col lg:flex-row p-6 gap-8">
          
          {/* Left Column - Image */}
          <div className="w-full lg:w-2/5 flex flex-col items-center">
            <div className="w-full aspect-[3/4] bg-gray-50 border border-gray-200 flex items-center justify-center overflow-hidden">
              <div className="relative w-full h-full">
                <Image
                  src={imgSrc}
                  alt={product?.title || "Product Image"}
                  fill
                  className="object-contain"
                  onError={() => setImgSrc("/placeholder.jpg")}
                />
              </div>
            </div>
          </div>

          {/* Right Column - Details */}
          <div className="w-full lg:w-3/5 flex flex-col justify-between space-y-5">
            
            {/* Author & Category */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">Author</span>
                <p className="text-base font-medium text-[#0A3982] mt-1">
                  {product?.author?.name || "N/A"}
                </p>
                {product?.author?.description && (
                  <p className="text-xs text-gray-600 mt-1 line-clamp-2 leading-relaxed">
                    {trimText(product?.author?.description, 100)}
                  </p>
                )}
              </div>
              <div>
                <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</span>
                <p className="text-base font-medium text-gray-900 mt-1 capitalize">
                  {product?.category} / {product?.subCategory}
                </p>
                <p className="text-xs text-gray-500 mt-1 capitalize">
                  Type: {product?.type}
                </p>
              </div>
            </div>

            <Separator className="bg-gray-200" />

            {/* Pricing & Badge */}
            <div className="flex items-center gap-4 flex-wrap">
              {product?.discount === 0 ? (
                <p className="text-3xl font-bold text-[#0A3982]">
                  {formatCurrency(product?.price)}
                </p>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <p className="text-3xl font-bold text-[#0A3982]">
                      {formatCurrency(Math.ceil(product?.discountedPrice))}
                    </p>
                    <p className="text-lg font-medium text-gray-400 line-through">
                      {formatCurrency(product?.price)}
                    </p>
                  </div>
                  <Badge className="bg-green-600 hover:bg-green-700 text-white border-0 px-3 py-1 rounded-none text-sm font-bold">
                    -{Math.ceil(product?.discount)}% OFF
                  </Badge>
                </>
              )}
            </div>

            <Separator className="bg-gray-200" />

            {/* Product Metadata Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-4 gap-x-6">
              <div className="flex items-start gap-2">
                <BookOpen size={16} className="text-gray-400 mt-0.5" />
                <div>
                  <span className="block text-xs text-gray-500 font-medium">Edition</span>
                  <span className="text-sm font-medium text-gray-900">{product?.edition || "N/A"}</span>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Calendar size={16} className="text-gray-400 mt-0.5" />
                <div>
                  <span className="block text-xs text-gray-500 font-medium">Published</span>
                  <span className="text-sm font-medium text-gray-900">{product?.published || "N/A"}</span>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Hash size={16} className="text-gray-400 mt-0.5" />
                <div>
                  <span className="block text-xs text-gray-500 font-medium">ISBN</span>
                  <span className="text-sm font-medium text-gray-900">{product?.isbn || "N/A"}</span>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Layers size={16} className="text-gray-400 mt-0.5" />
                <div>
                  <span className="block text-xs text-gray-500 font-medium">Pages</span>
                  <span className="text-sm font-medium text-gray-900">{product?.pageNumber || "N/A"}</span>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div>
                  <span className="block text-xs text-gray-500 font-medium">Publisher</span>
                  <span className="text-sm font-medium text-gray-900">{product?.publisher || "N/A"}</span>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div>
                  <span className="block text-xs text-gray-500 font-medium">Language</span>
                  <span className="text-sm font-medium text-gray-900">{product?.language || "N/A"}</span>
                </div>
              </div>
            </div>

            {/* Stock & Add to Cart */}
            <div className="bg-gray-50 p-4 border border-gray-200 flex items-center justify-between flex-wrap gap-4">
              <div className="flex flex-col gap-1">
                <StockStatus inStock={isInStock} />
                {product?.barcode && (
                  <span className="text-[10px] text-gray-400">Code: {product?.barcode}</span>
                )}
              </div>
              <Button
                onClick={onAddToCart}
                disabled={!isInStock}
                className="bg-[#0A3982] hover:bg-[#06275a] text-white rounded-none h-11 px-8 font-semibold flex items-center gap-2 transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <ShoppingCart size={18} /> Add to cart
              </Button>
            </div>

            {/* Description */}
            {product?.description && (
              <div className="border-t border-gray-200 pt-3">
                <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Description</span>
                <p className="text-sm text-gray-700 leading-relaxed line-clamp-3">
                  {product?.description}
                </p>
              </div>
            )}
            
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductQuickView;